/* eslint-disable react/no-this-in-sfc */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { Box, Button, Divider, IconButton, useTheme } from '@mui/material';
import { darken } from 'polished';
import styled, { css } from 'styled-components';

import { ReactComponent as CloseIcon } from '@/assets/icons/close.svg';
import { createDependency, deleteDependency, getAllRoadmapNodes } from '@/features/canvas/api';
import { StyledSelect } from '@/features/canvas/components/StyledSelect';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useShowToast } from '@/hooks/useShowToast';

import { HistoryActionType, useUndoRedo } from '../../../contexts/undo-redo-context';

const InitiativeAccordionContentDependencies = () => {
  const {
    filteredNodes,
    setFilteredNodes,
    selectedNodeId,
    setIsSelectingDependencies,
    isSelectingDependencies,
    setShowDependencies,
    filteredNodesRef,
    updateLastSavedTime,
    roadmapNodes,
    hiddenChildNodeIds,
    editingInitiativeId,
    roadmap,
  } = useEditorContext();
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const { addHistoryItem } = useUndoRedo();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteNodeDependencyMutation } = useMutation(deleteDependency, {
    onSuccess: (_, { masterNodeId, slaveNodeId }) => {
      const newFilteredNodes = filteredNodesRef.current.map(n => {
        if (n.id === slaveNodeId) {
          return {
            ...n,
            dependencies: n.dependencies?.filter(
              d => d.masterNodeId !== masterNodeId || d.slaveNodeId !== slaveNodeId
            ),
          };
        }
        return n;
      });
      setFilteredNodes(newFilteredNodes as FilteredNodes[]);
      // Invalidate nodes to update hidden children and dependency lists
      void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
    },
    onError: () => {
      showToast('error', t('editor.canvas.dependency_deletion_error'));
    },
  });

  const { mutateAsync: createNodeDependency } = useMutation(createDependency, {
    onSuccess: ({ masterNodeId, slaveNodeId }) => {
      const newFilteredNodes = filteredNodesRef.current.map(n => {
        if (n.id === slaveNodeId) {
          return {
            ...n,
            dependencies: [
              ...(n.dependencies || []),
              {
                masterNodeId,
                slaveNodeId,
              },
            ],
          };
        }
        return n;
      });

      updateLastSavedTime();

      setFilteredNodes(newFilteredNodes as FilteredNodes[]);
      // Invalidate nodes to update hidden children and dependency lists
      void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
    },
    onError: () => {
      showToast('error', t('editor.canvas.dependency_creation_error'));
    },
  });

  const node =
    filteredNodes.find(n => n.id === selectedNodeId) ||
    roadmapNodes.find(n => n.id === selectedNodeId) ||
    // When editing an initiative from another roadmap, find it by initiative ID
    (editingInitiativeId
      ? roadmapNodes.find(n => n.initiatives?.id === editingInitiativeId)
      : null);

  // Check if the current initiative is hidden using the definitive set from EditorContext
  const isInitiativeHidden = useMemo(() => {
    // When editing an initiative directly (not via node selection)
    if (editingInitiativeId && !selectedNodeId) {
      // Find the node that contains this initiative
      const nodeWithInitiative = roadmapNodes.find(n => n.initiatives?.id === editingInitiativeId);
      const nodeId = nodeWithInitiative?.id;

      return nodeId ? hiddenChildNodeIds.has(nodeId) : false;
    }

    // Standard case: using selectedNodeId
    if (!selectedNodeId) return false;
    return hiddenChildNodeIds.has(selectedNodeId);
  }, [selectedNodeId, hiddenChildNodeIds, editingInitiativeId, roadmapNodes]);

  // Get all initiatives for dropdown (excluding self and existing dependencies)
  const availableInitiatives = useMemo(() => {
    if (!isInitiativeHidden) return [];

    // Get the current node's ID (from selectedNodeId or derived from editingInitiativeId)
    const currentNodeId = selectedNodeId || node?.id;
    if (!currentNodeId) return [];

    // Get existing dependency IDs (initiatives this node depends on)
    const existingDependencyIds = (node?.dependencies || []).map(d => d.masterNodeId);

    // Return all roadmap initiatives except self and existing dependencies
    return roadmapNodes
      .filter(n => {
        return (
          n.initiatives?.id &&
          n.id &&
          n.id !== currentNodeId &&
          !existingDependencyIds.includes(n.id)
        );
      })
      .map(n => ({
        id: n.id!,
        title: n.initiatives?.title || t('untitled'),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [isInitiativeHidden, node, roadmapNodes, selectedNodeId, t]);

  const handleDeleteDependency = useCallback(
    async ({ masterNodeId, slaveNodeId }: { masterNodeId: number; slaveNodeId: number }) => {
      await deleteNodeDependencyMutation({ masterNodeId, slaveNodeId });
      addHistoryItem({
        type: HistoryActionType.DeleteDependency,
        async undo(resourceIds?: number[]) {
          const createdDependency = await createNodeDependency({
            masterNodeId: resourceIds?.[1] || masterNodeId!,
            slaveNodeId: resourceIds?.[0] || slaveNodeId,
          });
          this.redo = async (redoResourceIds?: number[]) => {
            await deleteNodeDependencyMutation({
              masterNodeId: redoResourceIds?.[1] || createdDependency.masterNodeId,
              slaveNodeId: redoResourceIds?.[0] || createdDependency.slaveNodeId,
            });
          };
          this.resourceIds = [
            resourceIds?.[0] || selectedNodeId! || slaveNodeId,
            resourceIds?.[1] || createdDependency.masterNodeId!,
          ];
        },
        redo: async (resourceIds?: number[]) => {
          await deleteNodeDependencyMutation({
            masterNodeId: resourceIds?.[1] || masterNodeId,
            slaveNodeId: resourceIds?.[0] || slaveNodeId || selectedNodeId!,
          });
        },
        resourceIds: [slaveNodeId || selectedNodeId!, masterNodeId],
      });
    },
    [deleteNodeDependencyMutation, addHistoryItem, selectedNodeId, createNodeDependency]
  );

  const dependenciesToNodesThatExist = useMemo(() => {
    // For hidden initiatives, check dependencies against all roadmap nodes
    const nodesToCheck = isInitiativeHidden ? roadmapNodes : filteredNodes;
    return (
      node?.dependencies?.filter(dependency =>
        nodesToCheck.some(fNode => fNode.id === dependency.masterNodeId)
      ) || []
    );
  }, [filteredNodes, roadmapNodes, node?.dependencies, isInitiativeHidden]);

  const handleInitiativeSelection = async (targetNodeId: number) => {
    // Use the actual node ID (could be selectedNodeId or derived from editingInitiativeId)
    const currentNodeId = selectedNodeId || node?.id;
    if (!currentNodeId || !targetNodeId) return;

    try {
      // Current initiative depends on the selected one
      await createNodeDependency({
        masterNodeId: targetNodeId,
        slaveNodeId: currentNodeId,
      });

      showToast('success', t('editor.canvas.dependency_created'));
      // Invalidate nodes to update hidden children and dependency lists
      void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
    } catch (error) {
      showToast('error', t('editor.canvas.dependency_creation_error'));
    }
  };

  return (
    <Box sx={{ margin: '1rem 0' }}>
      <Title>{t('editor.sidebar.dependencies')}</Title>
      <Divider variant="fullWidth" sx={{ borderColor: theme.palette.brand.backgroundDialog }} />
      <Box sx={{ padding: '1rem 0' }}>
        {isInitiativeHidden ? (
          // Hidden initiative - use dropdown selection
          <>
            {availableInitiatives.length > 0 ? (
              <StyledSelect
                placeholder={t('editor.sidebar.select_initiative_to_depend_on')}
                selectedId={null}
                options={availableInitiatives}
                onChange={value => {
                  if (value) {
                    void handleInitiativeSelection(value);
                  }
                }}
              />
            ) : (
              <NoOptionsMessage>{t('editor.sidebar.no_initiatives_available')}</NoOptionsMessage>
            )}
          </>
        ) : (
          // Visible initiative - use canvas selection
          <AddDependencyButton
            $isSelectingDependencies={isSelectingDependencies}
            onClick={() => {
              setIsSelectingDependencies(!isSelectingDependencies);
              setShowDependencies(false);
            }}
          >
            {t('editor.sidebar.select_dependencies')}
          </AddDependencyButton>
        )}

        {/* Depends On list */}
        {dependenciesToNodesThatExist.length > 0 && (
          <>
            <SubHeader>{t('editor.sidebar.depends_on')}</SubHeader>
            <Divider
              variant="fullWidth"
              sx={{ borderColor: theme.palette.brand.backgroundDialog, mb: 1 }}
            />
          </>
        )}
        {dependenciesToNodesThatExist.map(dependency => {
          // For hidden initiatives, search in all roadmap nodes
          const nodesToSearch = isInitiativeHidden ? roadmapNodes : filteredNodes;
          const dependencyNode = nodesToSearch.find(fNode => fNode.id === dependency.masterNodeId);

          return (
            <DependencyListItem key={`${dependency.masterNodeId}-${dependency.slaveNodeId}`}>
              <DependencyListItemTitle>
                {dependencyNode?.initiatives?.title || t('untitled')}
              </DependencyListItemTitle>
              <IconButton
                onClick={() =>
                  handleDeleteDependency({
                    masterNodeId: dependency.masterNodeId!,
                    slaveNodeId: dependency.slaveNodeId,
                  })
                }
                sx={{ padding: '0.5rem' }}
              >
                <CloseIcon />
              </IconButton>
            </DependencyListItem>
          );
        })}
      </Box>
    </Box>
  );
};

const Title = styled.div`
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 1rem 0;
  font-weight: 600;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const DependencyListItem = styled.div`
  display: flex;
  margin: 0.5rem 0;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 1rem 0.25rem 2rem;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  border-radius: 2rem;
  background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
`;

const AddDependencyButton = styled(Button)<{ $isSelectingDependencies: boolean }>`
  margin: 1rem 0 2rem;
  padding: 0.25rem 1.5rem;
  letter-spacing: 0.75px;
  border-radius: 2rem;
  background-color: ${({ theme }) => theme.palette.brand.white};
  text-transform: none;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  box-shadow: ${({ theme }) => theme.shadows[0]};
  &:hover {
    background-color: ${({ theme }) => darken(0.1, theme.palette.brand.white)};
  }
  ${({ theme, $isSelectingDependencies }) =>
    $isSelectingDependencies &&
    css`
      font-weight: 600;
      color: ${theme.palette.brand.white};
      background-color: ${theme.palette.brand.editorTabPrimary};

      &:hover {
        background-color: ${darken(0.1, theme.palette.brand.editorTabPrimary)};
      }
    `}
`;

const DependencyListItemTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SubHeader = styled.div`
  margin: 1.5rem 0 0.5rem;
  padding-left: 1rem;
  font-weight: 500;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const NoOptionsMessage = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
  font-size: 1.25rem;
`;

export { InitiativeAccordionContentDependencies };
